import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Apartment,
  AccountTree,
  AssignmentInd,
  Bolt,
  Category,
  CenterFocusStrong,
  CloudUpload,
  DescriptionOutlined,
  DeleteOutline,
  Description,
  Factory,
  FolderSpecial,
  Gavel,
  HubOutlined,
  Inventory2,
  Link as LinkIcon,
  Person,
  Place,
  Refresh,
  Rule,
  SchemaOutlined,
  Search,
  SettingsInputComponent,
  VisibilityOutlined,
  ZoomIn,
  ZoomOut,
} from '@mui/icons-material';
import ForceGraph2D from 'react-force-graph-2d';
import {
  deleteIngestionDocument,
  generateIngestionDocumentGraph,
  getIngestionDocumentGraph,
  getIngestionUploadProgress,
  listIngestionDocuments,
  reloadIngestionDocument,
  uploadStandaloneDocument,
  viewIngestionDocumentGraphVisualization,
  viewIngestionDocument,
} from './api/fileApi';

const NODE_TYPE_COLORS = {
  entity: '#1f77b4',
  relationship: '#d62728',
  relation: '#d62728',
  emotion: '#ff7f0e',
  unknown: '#7f7f7f',
};

const ENTITY_TYPE_COLORS = {
  // Canonical KG entity types
  stakeholder: '#1d4ed8',
  task: '#059669',
  asset: '#334155',
  location: '#0f766e',
  document: '#7c3aed',
  parameter: '#0e7490',
  requirement: '#ea580c',
  interface: '#be185d',
  glossaryterm: '#65a30d',
};

const ENTITY_TYPE_ALIASES = {
  organizations: 'stakeholder',
  actor: 'stakeholder',
  actors: 'stakeholder',
  stakeholders: 'stakeholder',
  locations: 'location',
  assets: 'asset',
  documents: 'document',
  parameters: 'parameter',
  glossary_terms: 'glossaryterm',
};

const hashEntityType = (value) => {
  let hash = 0;
  const input = String(value || '');
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) - hash + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const buildEntityTypeColor = (entityType) => {
  const hash = hashEntityType(entityType);
  const hue = hash % 360;
  const saturation = 58 + (hash % 18); // 58-75
  const lightness = 40 + (hash % 14); // 40-53
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const NODE_TYPE_LABELS = {
  entity: 'Entity',
  relationship: 'Relationship',
  relation: 'Relationship',
  emotion: 'Emotion',
  unknown: 'Unknown',
};

const ENTITY_TYPE_SHORT = {
  organization: 'ORG',
  project: 'PRJ',
  requirement: 'REQ',
  location: 'LOC',
  document: 'DOC',
  material_category: 'MAT-C',
  material_type: 'MAT-T',
  asset: 'AST',
  voltage: 'VLT',
  system: 'SYS',
  responsibility: 'RSP',
  contract: 'CTR',
  manufacturer: 'MFR',
  person: 'PER',
  standard: 'STD',
  discipline: 'DSC',
  technical_object: 'T-OBJ',
  material: 'MAT',
  property: 'PROP',
  measurement: 'MEAS',
  function: 'FUNC',
  process: 'PROC',
  condition: 'COND',
  class_ontology: 'CLS-O',
  class_project: 'CLS-P',
  object: 'OBJ',
  requirement_project: 'REQ-P',
  requirement_ontology: 'REQ-O',
  verificationplan_project: 'VP-P',
  verificationplan_ontology: 'VP-O',
  superclass_external: 'SUPER',
};

const getEntityTypeShort = (entityTypeRaw) => {
  const normalized = String(entityTypeRaw || '').trim().toLowerCase();
  if (!normalized) return 'UNKN';
  return ENTITY_TYPE_SHORT[normalized] || formatEntityType(normalized).toUpperCase();
};

const ENTITY_TYPE_ICONS = {
  organization: Apartment,
  project: FolderSpecial,
  requirement: Rule,
  location: Place,
  document: DescriptionOutlined,
  material_category: Inventory2,
  material_type: Category,
  asset: HubOutlined,
  voltage: Bolt,
  system: SettingsInputComponent,
  responsibility: AssignmentInd,
  contract: Gavel,
  manufacturer: Factory,
  person: Person,
  standard: DescriptionOutlined,
  discipline: SchemaOutlined,
  technical_object: HubOutlined,
  material: Inventory2,
  property: Category,
  measurement: Bolt,
  function: LinkIcon,
  process: AccountTree,
  condition: LinkIcon,
};

const getEntityTypeIcon = (entityTypeRaw) => {
  const normalized = String(entityTypeRaw || '').trim().toLowerCase();
  return ENTITY_TYPE_ICONS[normalized] || AccountTree;
};

const EDGE_COLORS = {
  has_as_source: '#FF9800',
  is_derived_from: '#2196F3',
  is_verification_of: '#4CAF50',
  is_derived_in: '#7E57C2',
  has_requirement: '#EF5350',
  refers_to: '#9E9E9E',
  default: '#78909C',
};

const getEdgeColor = (label) => {
  const normalized = String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return EDGE_COLORS[normalized] || EDGE_COLORS.default;
};

const formatEntityType = (value) => {
  const text = String(value || '').trim();
  if (!text) return 'Unspecified';
  return text
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getEntityTypeColor = (entityType) => {
  const rawKey = String(entityType || '').toLowerCase();
  if (!rawKey) return NODE_TYPE_COLORS.entity;

  const key = ENTITY_TYPE_ALIASES[rawKey] || rawKey;
  if (ENTITY_TYPE_COLORS[key]) return ENTITY_TYPE_COLORS[key];
  return buildEntityTypeColor(key);
};

const getNodeColor = (node) => {
  if (node?.type === 'entity') {
    return getEntityTypeColor(node?.entityType);
  }
  return NODE_TYPE_COLORS[node?.type] || NODE_TYPE_COLORS.unknown;
};

const withAlpha = (color, alpha = 1) => {
  const safeAlpha = Math.max(0, Math.min(1, Number(alpha) || 0));
  const value = String(color || '').trim();
  if (!value) return `rgba(33, 150, 243, ${safeAlpha})`;

  const hexMatch = value.match(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/);
  if (hexMatch) {
    const hex = hexMatch[1];
    const expanded = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    const r = parseInt(expanded.slice(0, 2), 16);
    const g = parseInt(expanded.slice(2, 4), 16);
    const b = parseInt(expanded.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
  }

  if (/^hsl\(/i.test(value)) {
    return value.replace(/^hsl\((.*)\)$/i, `hsla($1, ${safeAlpha})`);
  }

  if (/^rgb\(/i.test(value)) {
    return value.replace(/^rgb\((.*)\)$/i, `rgba($1, ${safeAlpha})`);
  }

  return value;
};

const normalizeGraphId = (value) => String(value ?? '').trim();

const normalizeLinkEndpointId = (value) => {
  if (value && typeof value === 'object') {
    return normalizeGraphId(value.id || value.node_id || value.value || '');
  }
  return normalizeGraphId(value);
};

const normalizeText = (value, fallback = '') => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

const normalizeList = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const normalizeObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

const normalizeConfidence = (value, fallback = null) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.min(1, parsed));
};

const pickFirstNumericValue = (source, keys, fallback = 0) => {
  if (!source || typeof source !== 'object') return fallback;
  for (const key of keys) {
    const raw = source?.[key];
    if (raw === null || typeof raw === 'undefined' || raw === '') continue;
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const pickFirstNumericFromSources = (sources, keys, fallback = 0) => {
  for (const source of sources || []) {
    const value = pickFirstNumericValue(source, keys, Number.NaN);
    if (Number.isFinite(value)) return value;
  }
  return fallback;
};

const pickFirstTextFromSources = (sources, keys, fallback = '-') => {
  for (const source of sources || []) {
    if (!source || typeof source !== 'object') continue;
    for (const key of keys) {
      const raw = source?.[key];
      if (raw === null || typeof raw === 'undefined') continue;
      const text = String(raw).trim();
      if (text) return text;
    }
  }
  return fallback;
};

const formatDateTimeLabel = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const getGraphSearchText = (node) => {
  const aliases = Array.isArray(node?.aliases) ? node.aliases.join(' ') : '';
  const mentions = Array.isArray(node?.mentions)
    ? node.mentions.map((mention) => [
      mention?.document_title,
      mention?.section,
      mention?.heading,
      mention?.chunk,
      mention?.extracted_text,
    ].filter(Boolean).join(' ')).join(' ')
    : '';
  const documents = Array.isArray(node?.documents)
    ? node.documents.map((doc) => doc?.document_title).filter(Boolean).join(' ')
    : '';
  return [
    node?.label,
    node?.name,
    node?.id,
    node?.canonicalName,
    node?.entityTypeRaw,
    aliases,
    mentions,
    documents,
  ].filter(Boolean).join(' ').toLowerCase();
};

const DocumentsTab = ({ showSuccess, showError }) => {
  const fileInputRef = useRef(null);
  const docsAutoRefreshRef = useRef(null);
  const documentGraphRef = useRef(null);
  const documentGraphContainerRef = useRef(null);
  const documentGraphResizeObserverRef = useRef(null);
  const documentGraphLinksFrameRef = useRef(null);
  const graphProgressPollRef = useRef(null);
  const graphProgressTickBusyRef = useRef(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState('');
  const [pages, setPages] = useState({ processed: 0, total: 0 });
  const [taskId, setTaskId] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [busyByDocumentId, setBusyByDocumentId] = useState({});
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [graphDialogOpen, setGraphDialogOpen] = useState(false);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphDocument, setGraphDocument] = useState(null);
  const [graphPayload, setGraphPayload] = useState({ nodes: [], edges: [], meta: {} });
  const [graphTaskId, setGraphTaskId] = useState('');
  const [graphTaskStatus, setGraphTaskStatus] = useState('');
  const [graphTaskProgress, setGraphTaskProgress] = useState(0);
  const [graphTaskStep, setGraphTaskStep] = useState('');
  const [graphTaskCounts, setGraphTaskCounts] = useState({ created: 0, updated: 0, errors: 0 });
  const [graphPersistedSummary, setGraphPersistedSummary] = useState({ nodes: 0, edges: 0 });
  const [graphExtractionRun, setGraphExtractionRun] = useState(null);
  const [graphHoveredNode, setGraphHoveredNode] = useState(null);
  const [selectedGraphNode, setSelectedGraphNode] = useState(null);
  const [graphHoveredEdge, setGraphHoveredEdge] = useState(null);
  const [selectedGraphEdge, setSelectedGraphEdge] = useState(null);
  const [graphDimensions, setGraphDimensions] = useState({ w: 0, h: 0 });
  const [visibleEntityTypes, setVisibleEntityTypes] = useState({});
  const [visibleRelationTypes, setVisibleRelationTypes] = useState({});
  const [graphNodeSearchTerm, setGraphNodeSearchTerm] = useState('');
  const [graphMinConfidence, setGraphMinConfidence] = useState('0');
  const [graphLayoutMode, setGraphLayoutMode] = useState('balanced');
  const [graphFocusMode, setGraphFocusMode] = useState('all');
  const [renderedGraphData, setRenderedGraphData] = useState({ nodes: [], links: [] });

  const graphRunMetadata = useMemo(() => {
    if (!graphExtractionRun || typeof graphExtractionRun !== 'object') return {};
    const metadata = graphExtractionRun?.metadata;
    if (!metadata || typeof metadata !== 'object') return {};
    return metadata;
  }, [graphExtractionRun]);

  const graphRunMetadataSources = useMemo(() => {
    const sources = [];
    if (graphRunMetadata && typeof graphRunMetadata === 'object') {
      sources.push(graphRunMetadata);
      if (graphRunMetadata.kg_summary && typeof graphRunMetadata.kg_summary === 'object') {
        sources.push(graphRunMetadata.kg_summary);
      }
      if (graphRunMetadata.summary && typeof graphRunMetadata.summary === 'object') {
        sources.push(graphRunMetadata.summary);
      }
      if (graphRunMetadata.metrics && typeof graphRunMetadata.metrics === 'object') {
        sources.push(graphRunMetadata.metrics);
      }
      if (graphRunMetadata.stats && typeof graphRunMetadata.stats === 'object') {
        sources.push(graphRunMetadata.stats);
      }
    }
    return sources;
  }, [graphRunMetadata]);

  const graphRunDetailRows = useMemo(() => {
    if (!graphExtractionRun) return [];

    const pagesProcessed = pickFirstNumericFromSources(graphRunMetadataSources, [
      'kg_pages_processed',
      'pages_processed',
      'processed_pages',
      'pages_ok',
    ]);
    const pagesFailed = pickFirstNumericFromSources(graphRunMetadataSources, [
      'kg_pages_failed',
      'pages_failed',
      'failed_pages',
      'pages_error',
    ]);
    const nodesProcessed = pickFirstNumericFromSources(graphRunMetadataSources, [
      'kg_nodes_processed',
      'nodes_processed',
      'processed_nodes',
      'nodes',
    ], graphPersistedSummary.nodes || 0);
    const edgesProcessed = pickFirstNumericFromSources(graphRunMetadataSources, [
      'kg_edges_processed',
      'edges_processed',
      'processed_edges',
      'relations_processed',
      'relationships_processed',
      'edges',
    ], graphPersistedSummary.edges || 0);
    const extractionErrors = pickFirstNumericFromSources(graphRunMetadataSources, [
      'kg_errors_count',
      'errors_count',
      'errors',
      'total_errors',
    ]);
    const failedPagesMessage = pickFirstTextFromSources(graphRunMetadataSources, [
      'failed_pages_detail',
      'failed_pages_message',
      'failure_reason',
      'error_detail',
      'error_message',
    ], '-');

    const baseRows = [
      { label: 'Run ID', value: String(graphExtractionRun?.run_id || graphExtractionRun?.id || '-') },
      { label: 'Status', value: String(graphExtractionRun?.status || 'unknown') },
      { label: 'Model', value: String(graphExtractionRun?.model_name || '-') },
      { label: 'Pages processed', value: pagesProcessed },
      { label: 'Pages failed', value: pagesFailed },
      { label: 'Nodes persisted', value: nodesProcessed },
      { label: 'Edges persisted', value: edgesProcessed },
      { label: 'Errors', value: extractionErrors },
      { label: 'Started', value: formatDateTimeLabel(graphExtractionRun?.started_at) },
      { label: 'Finished', value: formatDateTimeLabel(graphExtractionRun?.finished_at) },
    ];

    if ((pagesFailed > 0 || extractionErrors > 0) && failedPagesMessage !== '-') {
      baseRows.push({ label: 'Failure detail', value: failedPagesMessage });
    }

    return baseRows;
  }, [graphExtractionRun, graphRunMetadataSources, graphPersistedSummary.edges, graphPersistedSummary.nodes]);

  const stopGraphProgressPolling = useCallback(() => {
    if (graphProgressPollRef.current) {
      clearInterval(graphProgressPollRef.current);
      graphProgressPollRef.current = null;
    }
    graphProgressTickBusyRef.current = false;
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      setLoadingDocuments(true);
      const response = await listIngestionDocuments();
      setDocuments(Array.isArray(response?.documents) ? response.documents : []);
    } catch (error) {
      showError?.(error?.response?.data?.error || error?.message || 'Error loading documents.');
    } finally {
      setLoadingDocuments(false);
    }
  }, [showError]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => () => {
    stopGraphProgressPolling();
  }, [stopGraphProgressPolling]);

  const hasActiveDocumentProcessing = useMemo(() => {
    const ACTIVE_STATUS_CODES = new Set([1, 2, 6, 7]); // Uploading, Summarizing, Translating, Pending
    const ACTIVE_STATUS_LABELS = ['upload', 'summar', 'translat', 'pending', 'process'];

    return (documents || []).some((doc) => {
      if (doc?.graph_generation_in_progress) {
        return true;
      }

      const statusCode = Number(doc?.status);
      if (ACTIVE_STATUS_CODES.has(statusCode)) {
        return true;
      }

      const label = String(doc?.status_label || '').toLowerCase();
      return ACTIVE_STATUS_LABELS.some((token) => label.includes(token));
    });
  }, [documents]);

  useEffect(() => {
    const shouldAutoRefresh = Boolean(uploading || taskId || hasActiveDocumentProcessing);

    if (!shouldAutoRefresh) {
      if (docsAutoRefreshRef.current) {
        clearInterval(docsAutoRefreshRef.current);
        docsAutoRefreshRef.current = null;
      }
      return;
    }

    if (!docsAutoRefreshRef.current) {
      docsAutoRefreshRef.current = setInterval(() => {
        loadDocuments();
      }, 3000);
    }

    return () => {
      if (docsAutoRefreshRef.current) {
        clearInterval(docsAutoRefreshRef.current);
        docsAutoRefreshRef.current = null;
      }
    };
  }, [uploading, taskId, hasActiveDocumentProcessing, loadDocuments]);

  const setDocumentBusy = (documentId, isBusy) => {
    setBusyByDocumentId((previous) => {
      const next = { ...previous };
      if (isBusy) {
        next[documentId] = true;
      } else {
        delete next[documentId];
      }
      return next;
    });
  };

  const handleOpenPicker = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) return;

    const extension = String(file.name || '').toLowerCase();
    if (!extension.endsWith('.pdf') && !extension.endsWith('.xlsx')) {
      showError?.('Only .pdf and .xlsx files are allowed.');
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || uploading) return;

    try {
      setUploading(true);
      setProgress(0);
      setStep('Uploading file to server...');
      setPages({ processed: 0, total: 0 });
      setTaskId('');

      const response = await uploadStandaloneDocument(selectedFile, (evt) => {
        const totalBytes = Number(evt?.total || 0);
        const loadedBytes = Number(evt?.loaded || 0);
        if (totalBytes > 0) {
          const pct = Math.min(100, Math.max(0, Math.round((loadedBytes * 100) / totalBytes)));
          setProgress(pct);
        }
      });

      if (!response?.task_id) {
        showSuccess?.(response?.message || 'Document processed successfully.');
        setProgress(100);
        setStep('Completed');
        return;
      }

      setTaskId(String(response.task_id));

      const startedAt = Date.now();
      const maxPollingMs = 20 * 60 * 1000;

      while (true) {
        const progressResponse = await getIngestionUploadProgress(response.task_id);
        const nextProgress = Number(progressResponse?.progress_percentage || 0);
        const nextStep = String(progressResponse?.current_step || '').trim();
        const processedPages = Number(progressResponse?.processed_pages || 0);
        const totalPages = Number(progressResponse?.total_pages || 0);

        setProgress(Math.min(100, Math.max(0, Math.round(nextProgress))));
        setStep(nextStep || 'Processing on server...');
        setPages({
          processed: Math.max(0, processedPages),
          total: Math.max(0, totalPages),
        });

        if (progressResponse?.status === 'completed') {
          showSuccess?.('Document uploaded successfully.');
          await loadDocuments();
          break;
        }

        if (progressResponse?.status === 'failed') {
          throw new Error(progressResponse?.error_message || 'Error processing uploaded document.');
        }

        if ((Date.now() - startedAt) > maxPollingMs) {
          throw new Error('Processing timeout: document is still being processed on server.');
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    } catch (error) {
      showError?.(error?.response?.data?.error || error?.message || 'Error uploading document.');
    } finally {
      setUploading(false);
    }
  };

  const handleReloadDocument = async (documentId) => {
    if (!documentId) return;
    try {
      setDocumentBusy(documentId, true);
      const response = await reloadIngestionDocument(documentId);
      showSuccess?.(response?.message || 'Document reload queued successfully.');
      await loadDocuments();
    } catch (error) {
      showError?.(error?.response?.data?.error || error?.message || 'Error reloading document.');
    } finally {
      setDocumentBusy(documentId, false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!documentId) return;
    try {
      setDocumentBusy(documentId, true);
      const response = await deleteIngestionDocument(documentId);
      showSuccess?.(response?.message || 'Document deleted successfully.');
      await loadDocuments();
    } catch (error) {
      showError?.(error?.response?.data?.error || error?.message || 'Error deleting document.');
    } finally {
      setDocumentBusy(documentId, false);
    }
  };

  const handleRequestDeleteDocument = (document) => {
    if (!document?.id || busyByDocumentId[document.id]) return;
    setDocumentToDelete(document);
  };

  const handleConfirmDeleteDocument = async () => {
    const documentId = documentToDelete?.id;
    setDocumentToDelete(null);
    await handleDeleteDocument(documentId);
  };

  const handleOpenGraph = async (doc, forceGenerate = false) => {
    const documentId = doc?.id;
    if (!documentId) return;

    try {
      setDocumentBusy(documentId, true);
      setGraphLoading(true);
      setGraphDocument(doc);
      setGraphTaskId('');
      setGraphTaskStatus('');
      setGraphTaskProgress(0);
      setGraphTaskStep('');
      setGraphTaskCounts({ created: 0, updated: 0, errors: 0 });
      setGraphPersistedSummary({ nodes: 0, edges: 0 });
      setGraphExtractionRun(null);
      stopGraphProgressPolling();

      let response;
      if (forceGenerate) {
        response = await generateIngestionDocumentGraph(documentId, true);
        await loadDocuments();
      } else {
        try {
          // Always try cached graph first to avoid unnecessary regeneration on refresh.
          response = await getIngestionDocumentGraph(documentId);
        } catch (error) {
          const statusCode = Number(error?.response?.status || 0);
          if (statusCode !== 404) {
            throw error;
          }
          response = await generateIngestionDocumentGraph(documentId, false);
          await loadDocuments();
        }
      }

      const nextGraph = response?.graph || { nodes: [], edges: [], meta: {} };
      const queuedTaskId = String(response?.task_id || '').trim();

      if (queuedTaskId && !response?.graph) {
        setGraphDialogOpen(true);
        setGraphTaskId(queuedTaskId);
        setGraphTaskStatus(String(response?.status || 'processing'));
        setGraphTaskProgress(Number(response?.progress_percentage || 0));
        setGraphTaskStep(String(response?.current_step || 'Queued graph generation'));
        setGraphTaskCounts({
          created: Number(response?.created_count || 0),
          updated: Number(response?.updated_count || 0),
          errors: Number(response?.errors_count || 0),
        });

        const tick = async () => {
          if (graphProgressTickBusyRef.current) return;
          graphProgressTickBusyRef.current = true;
          try {
            const progressResponse = await getIngestionUploadProgress(queuedTaskId);
            const nextStatus = String(progressResponse?.status || 'processing').toLowerCase();
            const nextProgress = Number(progressResponse?.progress_percentage || 0);
            const nextStep = String(progressResponse?.current_step || '').trim();

            setGraphTaskStatus(nextStatus);
            setGraphTaskProgress(Math.min(100, Math.max(0, Math.round(nextProgress))));
            setGraphTaskStep(nextStep || 'Generating graph...');
            setGraphTaskCounts({
              created: Number(progressResponse?.created_count || 0),
              updated: Number(progressResponse?.updated_count || 0),
              errors: Number(progressResponse?.errors_count || 0),
            });

            if (nextStatus === 'completed') {
              stopGraphProgressPolling();
              const graphResponse = await getIngestionDocumentGraph(documentId);
              const generatedGraph = graphResponse?.graph || { nodes: [], edges: [], meta: {} };
              setGraphPayload({
                nodes: Array.isArray(generatedGraph?.nodes) ? generatedGraph.nodes : [],
                edges: Array.isArray(generatedGraph?.edges) ? generatedGraph.edges : [],
                meta: generatedGraph?.meta || {},
              });
              setGraphPersistedSummary({
                nodes: Number(graphResponse?.kg_persisted?.nodes || 0),
                edges: Number(graphResponse?.kg_persisted?.edges || 0),
              });
              setGraphExtractionRun(graphResponse?.kg_extraction_run || null);
              setGraphLoading(false);
              await loadDocuments();
              showSuccess?.('Graph generated successfully.');
            } else if (nextStatus === 'failed') {
              stopGraphProgressPolling();
              setGraphLoading(false);
              throw new Error(progressResponse?.error_message || 'Error generating graph.');
            }
          } catch (pollError) {
            stopGraphProgressPolling();
            setGraphLoading(false);
            showError?.(pollError?.response?.data?.error || pollError?.message || 'Error getting graph generation progress.');
          } finally {
            graphProgressTickBusyRef.current = false;
          }
        };

        await tick();
        if (!graphProgressPollRef.current) {
          graphProgressPollRef.current = setInterval(() => {
            tick();
          }, 1200);
        }

        if (forceGenerate) {
          showSuccess?.(response?.message || 'Graph generation queued.');
        } else {
          showSuccess?.(response?.message || 'Graph generation started.');
        }
        return;
      }

      setGraphPayload({
        nodes: Array.isArray(nextGraph?.nodes) ? nextGraph.nodes : [],
        edges: Array.isArray(nextGraph?.edges) ? nextGraph.edges : [],
        meta: nextGraph?.meta || {},
      });
      setGraphPersistedSummary({
        nodes: Number(response?.kg_persisted?.nodes || 0),
        edges: Number(response?.kg_persisted?.edges || 0),
      });
      setGraphExtractionRun(response?.kg_extraction_run || null);
      setGraphDialogOpen(true);
      setGraphLoading(false);

      if (!forceGenerate) {
        showSuccess?.('Graph loaded successfully.');
      } else {
        showSuccess?.(response?.message || 'Graph generated successfully.');
      }
    } catch (error) {
      setGraphLoading(false);
      showError?.(error?.response?.data?.error || error?.message || 'Error generating graph.');
    } finally {
      setDocumentBusy(documentId, false);
    }
  };

  const handleCloseGraphDialog = () => {
    stopGraphProgressPolling();
    setGraphDialogOpen(false);
    setGraphHoveredNode(null);
    setSelectedGraphNode(null);
    setGraphHoveredEdge(null);
    setSelectedGraphEdge(null);
    setGraphNodeSearchTerm('');
    setGraphTaskId('');
    setGraphTaskStatus('');
    setGraphTaskProgress(0);
    setGraphTaskStep('');
    setGraphFocusMode('all');
  };

  const graphData = useMemo(() => {
    const uniqueNodesById = new Map();
    for (const node of graphPayload?.nodes || []) {
      const normalizedId = normalizeGraphId(
        node?.id
        || node?.node_id
        || node?.uuid
        || node?.canonical_name
        || node?.label
        || node?.name
      );
      if (!normalizedId) continue;
      if (uniqueNodesById.has(normalizedId)) continue;

      const nodeType = normalizeText(node?.type, 'entity').toLowerCase();
      const entityTypeRaw = normalizeText(
        node?.entity_type
        || node?.node_type
        || node?.nodeType
        || node?.category
        || node?.node_type_ref,
        'unspecified'
      );
      const aliases = normalizeList(node?.aliases);
      const mentions = Number(
        node?.mentions_count
        || node?.mention_count
        || node?.mentionCount
        || (Array.isArray(node?.mentions) ? node.mentions.length : node?.mentions)
        || 1
      );
      const properties = normalizeObject(node?.properties);
      const confidence = normalizeConfidence(node?.confidence, null);

      uniqueNodesById.set(normalizedId, {
        id: normalizedId,
        label: node?.label || node?.canonical_name || node?.name || normalizedId,
        name: node?.name || node?.canonical_name || node?.label || normalizedId,
        canonicalName: normalizeText(node?.canonical_name || node?.canonicalName),
        description: normalizeText(node?.description),
        mentions: Number.isFinite(mentions) ? Math.max(1, mentions) : 1,
        mentionItems: Array.isArray(node?.mentions) ? node.mentions : [],
        documents: Array.isArray(node?.documents) ? node.documents : [],
        type: nodeType,
        entityTypeRaw,
        entityType: entityTypeRaw.toLowerCase(),
        aliases,
        externalIds: normalizeObject(node?.external_ids || node?.externalIds),
        mergeHistory: normalizeList(node?.merge_history || node?.mergeHistory),
        confidence,
        degree: Number(node?.degree || 0),
        incomingEdgesCount: Number(node?.incoming_edges_count || 0),
        outgoingEdgesCount: Number(node?.outgoing_edges_count || 0),
        properties,
        raw: node,
        color: getNodeColor({
          type: nodeType,
          entityType: entityTypeRaw.toLowerCase(),
        }),
      });
    }

    const normalizedNodes = Array.from(uniqueNodesById.values());
    const normalizedNodeIds = new Set(normalizedNodes.map((node) => node.id));

    const normalizedLinks = (graphPayload?.edges || [])
      .map((edge, idx) => {
        const sourceId = normalizeLinkEndpointId(edge?.source || edge?.source_id || edge?.from);
        const targetId = normalizeLinkEndpointId(edge?.target || edge?.target_id || edge?.to);
        const label = normalizeText(
          edge?.label || edge?.relation_type || edge?.relation_type_code || edge?.type,
          'related_to'
        );
        const confidence = normalizeConfidence(edge?.confidence, null);
        const weight = Number(edge?.weight);
        const normalizedWeight = Number.isFinite(weight)
          ? weight
          : (confidence ?? 1);

        return {
          id: normalizeGraphId(edge?.id || `${sourceId}-${targetId}-${idx}`),
          source: sourceId,
          target: targetId,
          label,
          relationTypeRaw: label,
          relationType: label.toLowerCase(),
          weight: normalizedWeight,
          confidence,
          evidenceText: normalizeText(edge?.evidence_text || edge?.evidenceText),
          isInferred: Boolean(edge?.is_inferred || edge?.isInferred),
          metadata: normalizeObject(edge?.metadata),
          provenance: normalizeObject(edge?.provenance),
          mentions: Array.isArray(edge?.mentions) ? edge.mentions : [],
          documents: Array.isArray(edge?.documents) ? edge.documents : [],
          documentId: edge?.document_id,
          documentTitle: normalizeText(edge?.document_title),
          chunkId: edge?.chunk_id,
          chunkPageNumber: edge?.chunk_page_number,
          extractionRunId: edge?.extraction_run_id,
          properties: normalizeObject(edge?.properties),
          raw: edge,
          color: getEdgeColor(label),
        };
      })
      .filter((link) => link.source && link.target && normalizedNodeIds.has(link.source) && normalizedNodeIds.has(link.target));

    return {
      nodes: normalizedNodes,
      links: normalizedLinks,
    };
  }, [graphPayload]);

  const hasGraphVisualization = useMemo(() => {
    const artifacts = graphPayload?.meta?.langextract?.artifacts;
    return Boolean(artifacts && String(artifacts.html_path || '').trim());
  }, [graphPayload]);

  useEffect(() => () => {
    if (documentGraphLinksFrameRef.current) {
      cancelAnimationFrame(documentGraphLinksFrameRef.current);
      documentGraphLinksFrameRef.current = null;
    }
    if (documentGraphResizeObserverRef.current) {
      documentGraphResizeObserverRef.current.disconnect();
      documentGraphResizeObserverRef.current = null;
    }
  }, []);

  useEffect(() => {
    const nextVisibleTypes = {};
    for (const node of graphData.nodes || []) {
      if (node.type !== 'entity') continue;
      const typeKey = node.entityTypeRaw || 'Unspecified';
      nextVisibleTypes[typeKey] = true;
    }
    setVisibleEntityTypes(nextVisibleTypes);
  }, [graphData.nodes]);

  useEffect(() => {
    const nextVisibleRelationTypes = {};
    for (const link of graphData.links || []) {
      const relationKey = link.relationTypeRaw || link.label || 'related_to';
      nextVisibleRelationTypes[relationKey] = true;
    }
    setVisibleRelationTypes(nextVisibleRelationTypes);
  }, [graphData.links]);

  const typeCounts = useMemo(() => {
    const counts = {};
    for (const node of graphData.nodes || []) {
      if (node.type !== 'entity') continue;
      const typeKey = node.entityTypeRaw || 'Unspecified';
      counts[typeKey] = (counts[typeKey] || 0) + 1;
    }
    return counts;
  }, [graphData.nodes]);

  const sortedEntityTypes = useMemo(
    () => Object.keys(typeCounts).sort(),
    [typeCounts]
  );

  const relationTypeCounts = useMemo(() => {
    const counts = {};
    for (const link of graphData.links || []) {
      const relationKey = link.relationTypeRaw || link.label || 'related_to';
      counts[relationKey] = (counts[relationKey] || 0) + 1;
    }
    return counts;
  }, [graphData.links]);

  const sortedRelationTypes = useMemo(
    () => Object.keys(relationTypeCounts).sort(),
    [relationTypeCounts]
  );

  const graphCoverageSummary = useMemo(() => {
    const nodeCount = graphData.nodes.length;
    const edgeCount = graphData.links.length;
    const isolatedCount = Math.max(0, nodeCount - new Set(
      (graphData.links || []).flatMap((link) => [link.source, link.target])
    ).size);
    const mentionsTotal = (graphData.nodes || []).reduce((acc, node) => acc + Number(node.mentions || 0), 0);
    return {
      nodeCount,
      edgeCount,
      isolatedCount,
      mentionsTotal,
    };
  }, [graphData]);

  const filteredGraphData = useMemo(() => {
    const minConfidence = Math.max(0, Math.min(1, Number(graphMinConfidence) || 0));
    const allowedNodeIds = new Set();
    const nodes = [];

    for (const node of graphData.nodes || []) {
      if (node.type === 'entity') {
        const typeKey = node.entityTypeRaw || 'Unspecified';
        if (!visibleEntityTypes[typeKey]) {
          continue;
        }
      }
      nodes.push(node);
      allowedNodeIds.add(node.id);
    }

    let focusNodeIds = null;
    if (graphFocusMode === 'neighbors' && selectedGraphNode?.id) {
      focusNodeIds = new Set([selectedGraphNode.id]);
      for (const link of graphData.links || []) {
        const sourceId = typeof link.source === 'object' ? link.source?.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target?.id : link.target;
        if (sourceId === selectedGraphNode.id) focusNodeIds.add(targetId);
        if (targetId === selectedGraphNode.id) focusNodeIds.add(sourceId);
      }
    }

    const getEndpointId = (endpoint) => (typeof endpoint === 'object' ? endpoint?.id : endpoint);

    const links = (graphData.links || []).filter((link) => {
      const sourceId = getEndpointId(link.source);
      const targetId = getEndpointId(link.target);
      const relationKey = link.relationTypeRaw || link.label || 'related_to';
      return (
        allowedNodeIds.has(sourceId)
        && allowedNodeIds.has(targetId)
        && (!focusNodeIds || (focusNodeIds.has(sourceId) && focusNodeIds.has(targetId)))
        && Boolean(visibleRelationTypes[relationKey])
        && Number(link.confidence ?? 0) >= minConfidence
      );
    });

    const visibleIds = new Set();
    for (const link of links) {
      visibleIds.add(getEndpointId(link.source));
      visibleIds.add(getEndpointId(link.target));
    }

    const filteredNodes = nodes.filter((node) => {
      if (focusNodeIds) return focusNodeIds.has(node.id);
      if (links.length === 0) return allowedNodeIds.has(node.id);
      return visibleIds.has(node.id);
    });

    return { nodes: filteredNodes, links };
  }, [graphData.nodes, graphData.links, visibleEntityTypes, visibleRelationTypes, graphMinConfidence, graphFocusMode, selectedGraphNode]);

  useEffect(() => {
    if (documentGraphLinksFrameRef.current) {
      cancelAnimationFrame(documentGraphLinksFrameRef.current);
      documentGraphLinksFrameRef.current = null;
    }

    const stagedNodes = Array.isArray(filteredGraphData?.nodes) ? filteredGraphData.nodes : [];
    const stagedLinks = Array.isArray(filteredGraphData?.links) ? filteredGraphData.links : [];

    setRenderedGraphData({ nodes: stagedNodes, links: [] });

    documentGraphLinksFrameRef.current = requestAnimationFrame(() => {
      setRenderedGraphData({ nodes: stagedNodes, links: stagedLinks });
      documentGraphLinksFrameRef.current = null;
    });

    return () => {
      if (documentGraphLinksFrameRef.current) {
        cancelAnimationFrame(documentGraphLinksFrameRef.current);
        documentGraphLinksFrameRef.current = null;
      }
    };
  }, [filteredGraphData]);

  const normalizedGraphSearchTerm = useMemo(
    () => String(graphNodeSearchTerm || '').trim().toLowerCase(),
    [graphNodeSearchTerm]
  );

  const graphSearchMatches = useMemo(() => {
    if (!normalizedGraphSearchTerm) return [];

    return (filteredGraphData.nodes || [])
      .filter((node) => {
        return getGraphSearchText(node).includes(normalizedGraphSearchTerm);
      })
      .sort((a, b) => {
        const left = String(a?.label || a?.name || a?.id || '');
        const right = String(b?.label || b?.name || b?.id || '');
        return left.localeCompare(right);
      });
  }, [filteredGraphData.nodes, normalizedGraphSearchTerm]);

  const handleToggleEntityType = (entityTypeRaw) => {
    setVisibleEntityTypes((previous) => ({
      ...previous,
      [entityTypeRaw]: !previous[entityTypeRaw],
    }));
  };

  const handleShowAllEntityTypes = () => {
    setVisibleEntityTypes((previous) => {
      const next = {};
      for (const key of Object.keys(previous)) {
        next[key] = true;
      }
      return next;
    });
  };

  const handleToggleRelationType = (relationTypeRaw) => {
    setVisibleRelationTypes((previous) => ({
      ...previous,
      [relationTypeRaw]: !previous[relationTypeRaw],
    }));
  };

  const handleShowAllRelationTypes = () => {
    setVisibleRelationTypes((previous) => {
      const next = {};
      for (const key of Object.keys(previous)) {
        next[key] = true;
      }
      return next;
    });
  };

  const handleHideAllRelationTypes = () => {
    setVisibleRelationTypes((previous) => {
      const next = {};
      for (const key of Object.keys(previous)) {
        next[key] = false;
      }
      return next;
    });
  };

  const handleHideAllEntityTypes = () => {
    setVisibleEntityTypes((previous) => {
      const next = {};
      for (const key of Object.keys(previous)) {
        next[key] = false;
      }
      return next;
    });
  };

  const graphLegend = useMemo(() => {
    const entityTypeItems = [];
    const nonEntityTypeItems = [];
    const usedEntityTypes = new Set();
    const usedNonEntityTypes = new Set();

    for (const node of filteredGraphData.nodes || []) {
      const nodeType = node.type || 'unknown';
      if (nodeType === 'entity') {
        const entityType = node.entityType || 'unspecified';
        usedEntityTypes.add(entityType);
      } else {
        usedNonEntityTypes.add(nodeType);
      }
    }

    for (const entityType of Array.from(usedEntityTypes).sort()) {
      entityTypeItems.push({
        key: `entity:${entityType}`,
        label: `Entity: ${formatEntityType(entityType)}`,
        color: getEntityTypeColor(entityType),
      });
    }

    for (const nodeType of Array.from(usedNonEntityTypes).sort()) {
      nonEntityTypeItems.push({
        key: `type:${nodeType}`,
        label: NODE_TYPE_LABELS[nodeType] || nodeType,
        color: NODE_TYPE_COLORS[nodeType] || NODE_TYPE_COLORS.unknown,
      });
    }

    return [...entityTypeItems, ...nonEntityTypeItems];
  }, [filteredGraphData.nodes]);

  const graphNodeMap = useMemo(() => {
    const map = {};
    for (const node of filteredGraphData.nodes || []) {
      map[node.id] = node;
    }
    return map;
  }, [filteredGraphData.nodes]);

  useEffect(() => {
    if (!selectedGraphNode) return;
    if (!graphNodeMap[selectedGraphNode.id]) {
      setSelectedGraphNode(null);
    }
  }, [graphNodeMap, selectedGraphNode]);

  useEffect(() => {
    if (!selectedGraphEdge) return;
    const exists = (filteredGraphData.links || []).some((link) => link.id === selectedGraphEdge.id);
    if (!exists) {
      setSelectedGraphEdge(null);
    }
  }, [filteredGraphData.links, selectedGraphEdge]);

  const selectedGraphNodeRelationships = useMemo(() => {
    if (!selectedGraphNode) {
      return { incoming: [], outgoing: [] };
    }

    const incoming = [];
    const outgoing = [];

    for (const link of filteredGraphData.links || []) {
      const sourceId = typeof link.source === 'object' ? link.source?.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target?.id : link.target;

      if (sourceId === selectedGraphNode.id && graphNodeMap[targetId]) {
        outgoing.push({
          node: graphNodeMap[targetId],
          link,
        });
      }

      if (targetId === selectedGraphNode.id && graphNodeMap[sourceId]) {
        incoming.push({
          node: graphNodeMap[sourceId],
          link,
        });
      }
    }

    return { incoming, outgoing };
  }, [filteredGraphData.links, graphNodeMap, selectedGraphNode]);

  const handleGraphNodeClick = useCallback((node) => {
    if (!node) return;
    const resolvedNode = graphNodeMap[node.id] || node;
    setSelectedGraphEdge(null);
    setSelectedGraphNode((previous) => (previous?.id === resolvedNode.id ? null : resolvedNode));
  }, [graphNodeMap]);

  const handleGraphLinkClick = useCallback((link) => {
    if (!link) return;
    setSelectedGraphNode(null);
    setSelectedGraphEdge((previous) => (previous?.id === link.id ? null : link));
  }, []);

  const handleGraphZoomIn = () => {
    if (!documentGraphRef.current) return;
    const currentZoom = documentGraphRef.current.zoom();
    documentGraphRef.current.zoom(currentZoom * 1.4, 300);
  };

  const handleGraphZoomOut = () => {
    if (!documentGraphRef.current) return;
    const currentZoom = documentGraphRef.current.zoom();
    documentGraphRef.current.zoom(currentZoom / 1.4, 300);
  };

  const handleGraphZoomFit = () => {
    documentGraphRef.current?.zoomToFit(400, 40);
  };

  const navigateToGraphNode = useCallback((node) => {
    setSelectedGraphEdge(null);
    setSelectedGraphNode(node);
    if (documentGraphRef.current && node?.x != null && node?.y != null) {
      documentGraphRef.current.centerAt(node.x, node.y, 500);
      documentGraphRef.current.zoom(2.8, 500);
    }
  }, []);

  const selectedGraphEdgeEndpoints = useMemo(() => {
    if (!selectedGraphEdge) return { source: null, target: null };
    const sourceId = typeof selectedGraphEdge.source === 'object' ? selectedGraphEdge.source?.id : selectedGraphEdge.source;
    const targetId = typeof selectedGraphEdge.target === 'object' ? selectedGraphEdge.target?.id : selectedGraphEdge.target;
    return {
      source: graphNodeMap[sourceId] || null,
      target: graphNodeMap[targetId] || null,
    };
  }, [graphNodeMap, selectedGraphEdge]);

  useEffect(() => {
    if (!documentGraphRef.current) return;
    const graphApi = documentGraphRef.current;
    const chargeStrength = graphLayoutMode === 'compact' ? -520 : -850;
    const linkDistance = graphLayoutMode === 'compact' ? 150 : 260;
    graphApi.d3Force('charge')?.strength(chargeStrength).distanceMax(graphLayoutMode === 'compact' ? 520 : 850);
    graphApi.d3Force('link')?.distance(linkDistance).strength(graphLayoutMode === 'compact' ? 0.38 : 0.28);
    graphApi.d3ReheatSimulation?.();
  }, [graphLayoutMode, renderedGraphData.nodes.length, renderedGraphData.links.length]);

  const handleViewDocument = async (doc) => {
    const documentId = doc?.id;
    if (!documentId) return;

    try {
      setDocumentBusy(documentId, true);
      const fileBlob = await viewIngestionDocument(documentId);
      const objectUrl = window.URL.createObjectURL(fileBlob);
      const popup = window.open(objectUrl, '_blank', 'noopener,noreferrer');

      if (!popup) {
        window.URL.revokeObjectURL(objectUrl);
        throw new Error('Popup blocked by browser. Please allow popups for this site.');
      }

      // Keep URL alive briefly so the new tab can load it.
      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60000);
    } catch (error) {
      showError?.(error?.response?.data?.error || error?.message || 'Error opening document.');
    } finally {
      setDocumentBusy(documentId, false);
    }
  };

  const handleViewGraphVisualization = async (doc) => {
    const documentId = doc?.id;
    if (!documentId) return;

    try {
      setDocumentBusy(documentId, true);
      const fileBlob = await viewIngestionDocumentGraphVisualization(documentId);
      const objectUrl = window.URL.createObjectURL(fileBlob);
      const popup = window.open(objectUrl, '_blank', 'noopener,noreferrer');

      if (!popup) {
        window.URL.revokeObjectURL(objectUrl);
        throw new Error('Popup blocked by browser. Please allow popups for this site.');
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60000);
    } catch (error) {
      showError?.(error?.response?.data?.error || error?.message || 'Visualization is not available yet.');
    } finally {
      setDocumentBusy(documentId, false);
    }
  };

  const renderProjectNames = (doc) => {
    const names = Array.isArray(doc?.project_names) ? doc.project_names.filter(Boolean) : [];
    if (names.length === 0) {
      return <Chip size="small" label="No project" variant="outlined" />;
    }
    return (
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
        {names.map((name, idx) => (
          <Chip key={`${doc.id}-project-${idx}`} size="small" label={name} color="info" variant="outlined" sx={{ mb: 0.5 }} />
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 0 }}>
      <Paper sx={{ p: { xs: 1.25, sm: 1.5, lg: 2 }, border: '1px solid', borderColor: 'divider', width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Stack spacing={{ xs: 1, sm: 1.25 }} sx={{ height: '100%', minHeight: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Documents
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                Upload standalone documents not related to any project.
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                startIcon={<Description />}
                onClick={handleOpenPicker}
                disabled={uploading}
              >
                Select
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
              <Tooltip title="Refresh documents">
                <span>
                  <IconButton size="small" onClick={loadDocuments} disabled={loadingDocuments}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {selectedFile && (
                <Chip
                  label={selectedFile.name}
                  size="small"
                  sx={{
                    maxWidth: { xs: '100%', sm: 220, md: 320 },
                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                  }}
                />
              )}
            </Stack>
          </Box>

          <Alert severity="info" sx={{ py: { xs: 0.25, sm: 0.5 } }}>
            This section is for documents without project relation. For project documents, use Projects {'>'} Documents {'>'} Upload Project Document.
          </Alert>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.xlsx"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {(uploading || progress > 0) && (
            <Box>
              <LinearProgress
                variant={progress === 100 && uploading ? 'indeterminate' : 'determinate'}
                value={progress}
                sx={{ mb: 0.75 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {step || `Uploading file: ${progress}%`}
              </Typography>
              {pages.total > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {`Pages: ${pages.processed}/${pages.total}`}
                </Typography>
              )}
              {taskId && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {`Task ID: ${taskId}`}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ pt: { xs: 0.5, sm: 1 }, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Loaded Documents
              </Typography>
            </Stack>

            {documents.length === 0 ? (
              <Alert severity="info">No documents loaded yet.</Alert>
            ) : (
              <Paper variant="outlined" sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Table size="small" sx={{ minWidth: 760 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Projects</TableCell>
                      <TableCell>Graph</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map((doc) => {
                      const documentBusy = Boolean(busyByDocumentId[doc.id]);
                      return (
                        <TableRow key={doc.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {doc.title || doc.file_name || `Document ${doc.id}`}
                            </Typography>
                            {doc.file_name && (
                              <Typography variant="caption" color="text.secondary">
                                {doc.file_name}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={doc.status_label || 'Unknown'} />
                          </TableCell>
                          <TableCell>{renderProjectNames(doc)}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.75}>
                              <Tooltip title={doc?.has_knowledge_graph ? 'View New KG graph' : 'Generate New KG graph'}>
                                <IconButton
                                  size="small"
                                  color={doc?.graph_generation_in_progress ? 'warning' : (doc?.has_knowledge_graph ? 'success' : 'primary')}
                                  onClick={() => handleOpenGraph(doc, false)}
                                  disabled={documentBusy}
                                  aria-label="Document new graph"
                                >
                                  <AccountTree fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                            {doc?.graph_generation_in_progress && (
                              <Box sx={{ mt: 0.75, minWidth: 210 }}>
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  label={`New KG ${doc?.graph_generation_status || 'processing'}`}
                                  sx={{ mb: 0.5 }}
                                />
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, Math.max(0, Number(doc?.graph_generation_progress || 0)))}
                                  sx={{ height: 6, borderRadius: 999 }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                  {`${Math.round(Number(doc?.graph_generation_progress || 0))}% ${doc?.graph_generation_step ? `· ${doc.graph_generation_step}` : ''}`}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                              <Tooltip title="View document">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewDocument(doc)}
                                  disabled={documentBusy}
                                  aria-label="View document"
                                >
                                  <VisibilityOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reload document">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleReloadDocument(doc.id)}
                                  disabled={documentBusy}
                                  aria-label="Reload document"
                                >
                                  <Refresh fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete document">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRequestDeleteDocument(doc)}
                                  disabled={documentBusy}
                                  aria-label="Delete document"
                                >
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Box>
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(documentToDelete)}
        onClose={() => setDocumentToDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderTop: '3px solid',
            borderColor: 'error.main',
            borderRadius: 2,
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.18)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, pb: 1, px: 2.5, pt: 2.25 }}>
          <Box sx={{ display: 'grid', placeItems: 'center', width: 34, height: 34, flexShrink: 0, borderRadius: '50%', bgcolor: 'error.light', color: 'error.dark' }}>
            <DeleteOutline fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              Delete document?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {documentToDelete
              ? `This will permanently delete "${documentToDelete.title || documentToDelete.file_name || 'this document'}" and its processed data.`
              : 'This action cannot be undone.'}
          </Typography>
          <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>
            The document and its processed data will be removed.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, px: 2.5, py: 1.5 }}>
          <Button onClick={() => setDocumentToDelete(null)} sx={{ minWidth: 96 }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteDocument}
            disabled={!documentToDelete || Boolean(busyByDocumentId[documentToDelete?.id])}
            sx={{ minWidth: 96 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={graphDialogOpen}
        onClose={handleCloseGraphDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            maxWidth: '95vw',
            height: '92vh',
          },
        }}
      >
        <DialogTitle>
          {`New KG Graph - ${graphDocument?.title || graphDocument?.file_name || 'Document'}`}
        </DialogTitle>
        <DialogContent dividers>
          {graphLoading ? (
            <Box sx={{ py: 1 }}>
              <LinearProgress
                variant={graphTaskProgress > 0 ? 'determinate' : 'indeterminate'}
                value={graphTaskProgress > 0 ? graphTaskProgress : 0}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                {graphTaskStep || 'Generating knowledge graph...'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {`Progress: ${Math.max(0, Math.round(graphTaskProgress || 0))}%`}
              </Typography>
              {graphTaskStatus && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {`Status: ${graphTaskStatus}`}
                </Typography>
              )}
              {graphTaskId && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {`Task ID: ${graphTaskId}`}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {`Generated payload nodes/edges: ${graphTaskCounts.created}/${graphTaskCounts.updated}`}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {`Task errors: ${graphTaskCounts.errors}`}
              </Typography>
            </Box>
          ) : graphData.nodes.length === 0 ? (
            <Alert severity="info">No graph relationships found yet for this document.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, height: 'min(76dvh, 680px)', minHeight: { xs: 420, lg: 360 }, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{ width: { xs: '100%', lg: 220 }, minWidth: 0, maxHeight: { xs: '32%', lg: 'none' }, flexShrink: 0, borderRight: { xs: 'none', lg: '1px solid' }, borderBottom: { xs: '1px solid', lg: 'none' }, borderColor: 'divider', overflow: 'auto', bgcolor: '#fafafa', p: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block', color: 'text.secondary' }}>
                  Entity Types
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                  <Button size="small" variant="text" onClick={handleShowAllEntityTypes} sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.75 }}>
                    All
                  </Button>
                  <Button size="small" variant="text" onClick={handleHideAllEntityTypes} sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.75 }}>
                    None
                  </Button>
                </Stack>
                <FormGroup row>
                  {sortedEntityTypes.map((entityTypeRaw) => (
                    <FormControlLabel
                      key={entityTypeRaw}
                      sx={{ mr: 1.5, mb: 0.25, '& .MuiFormControlLabel-label': { ml: 0.25 } }}
                      control={(
                        <Checkbox
                          size="small"
                          checked={Boolean(visibleEntityTypes[entityTypeRaw])}
                          onChange={() => handleToggleEntityType(entityTypeRaw)}
                          sx={{
                            py: 0.1,
                            color: getEntityTypeColor(entityTypeRaw),
                            '&.Mui-checked': {
                              color: getEntityTypeColor(entityTypeRaw),
                            },
                          }}
                        />
                      )}
                      label={(
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.68rem' }}>
                            {getEntityTypeShort(entityTypeRaw)}
                          </Typography>
                          <Chip label={typeCounts[entityTypeRaw] || 0} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700 }} />
                        </Stack>
                      )}
                    />
                  ))}
                </FormGroup>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: 'text.secondary' }}>
                  Legend
                </Typography>
                <Stack spacing={0.4}>
                  {graphLegend.map((item) => (
                    <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.6, color: 'text.secondary' }}>
                  Node Search
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by node name"
                  value={graphNodeSearchTerm}
                  onChange={(event) => setGraphNodeSearchTerm(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Layout"
                  value={graphLayoutMode}
                  onChange={(event) => setGraphLayoutMode(event.target.value)}
                  sx={{ mt: 0.8 }}
                >
                  <MenuItem value="balanced">Balanced</MenuItem>
                  <MenuItem value="compact">Compact</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  fullWidth
                  label="Min confidence"
                  value={graphMinConfidence}
                  onChange={(event) => setGraphMinConfidence(event.target.value)}
                  sx={{ mt: 0.8 }}
                  helperText="0.0 to 1.0"
                />
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.8 }}>
                  <Button size="small" variant={graphFocusMode === 'all' ? 'contained' : 'outlined'} onClick={() => setGraphFocusMode('all')}>
                    Full
                  </Button>
                  <Button size="small" variant={graphFocusMode === 'neighbors' ? 'contained' : 'outlined'} onClick={() => setGraphFocusMode('neighbors')} disabled={!selectedGraphNode}>
                    Neighbors
                  </Button>
                </Stack>
                {!normalizedGraphSearchTerm ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
                    Type text to list matching nodes.
                  </Typography>
                ) : graphSearchMatches.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
                    No nodes found.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6, mb: 0.35 }}>
                      {`${graphSearchMatches.length} node(s) found`}
                    </Typography>
                    <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto' }}>
                      {graphSearchMatches.map((node) => (
                        <ListItemButton
                          key={`search-${node.id}`}
                          sx={{ py: 0.3, px: 0.5 }}
                          onClick={() => navigateToGraphNode(node)}
                        >
                          {React.createElement(getEntityTypeIcon(node.entityTypeRaw || node.entityType || node.type || 'unknown'), {
                            fontSize: 'inherit',
                            style: { color: node.color || '#607d8b', marginRight: 6, fontSize: '0.95rem' },
                          })}
                          <Chip
                            size="small"
                            label={getEntityTypeShort(node.entityTypeRaw || node.entityType || node.type || 'unknown')}
                            sx={{ height: 16, fontSize: '0.58rem', mr: 0.5, bgcolor: node.color || '#607d8b', color: '#fff', fontWeight: 700 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              position: 'relative',
                              display: 'block',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              pr: 1,
                              '&::after': {
                                content: '" |"',
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                color: 'text.disabled',
                                fontWeight: 700,
                              },
                            }}
                          >
                            {node.label || node.name || node.id}
                          </Typography>
                        </ListItemButton>
                      ))}
                    </List>
                  </>
                )}

                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: 'text.secondary' }}>
                  Relation Types
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                  <Button size="small" variant="text" onClick={handleShowAllRelationTypes} sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.75 }}>
                    All
                  </Button>
                  <Button size="small" variant="text" onClick={handleHideAllRelationTypes} sx={{ fontSize: '0.65rem', minWidth: 0, px: 0.75 }}>
                    None
                  </Button>
                </Stack>
                <FormGroup>
                  {sortedRelationTypes.map((relationTypeRaw) => (
                    <FormControlLabel
                      key={relationTypeRaw}
                      sx={{ mr: 0, mb: 0.2, '& .MuiFormControlLabel-label': { ml: 0.25 } }}
                      control={(
                        <Checkbox
                          size="small"
                          checked={Boolean(visibleRelationTypes[relationTypeRaw])}
                          onChange={() => handleToggleRelationType(relationTypeRaw)}
                          sx={{ py: 0.1 }}
                        />
                      )}
                      label={(
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getEdgeColor(relationTypeRaw), flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.66rem' }} noWrap>
                            {relationTypeRaw}
                          </Typography>
                          <Chip label={relationTypeCounts[relationTypeRaw] || 0} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700 }} />
                        </Stack>
                      )}
                    />
                  ))}
                </FormGroup>
              </Box>

              <Box
                ref={(el) => {
                  if (documentGraphResizeObserverRef.current) {
                    documentGraphResizeObserverRef.current.disconnect();
                    documentGraphResizeObserverRef.current = null;
                  }

                  documentGraphContainerRef.current = el;
                  if (!el) return;

                  const resizeObserver = new ResizeObserver((entries) => {
                    for (const entry of entries) {
                      const { width, height } = entry.contentRect;
                      if (width > 0 && height > 0) {
                        setGraphDimensions({ w: Math.round(width), h: Math.round(height) });
                      }
                    }
                  });

                  resizeObserver.observe(el);
                  documentGraphResizeObserverRef.current = resizeObserver;
                }}
                sx={{ width: '100%', flex: 1, minWidth: 0, minHeight: 0, position: 'relative', bgcolor: '#f0f4f8' }}
              >
                <Paper elevation={2} sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.95)' }}>
                  <Tooltip title="Zoom in" placement="left">
                    <IconButton size="small" onClick={handleGraphZoomIn}>
                      <ZoomIn fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Divider />
                  <Tooltip title="Zoom out" placement="left">
                    <IconButton size="small" onClick={handleGraphZoomOut}>
                      <ZoomOut fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Divider />
                  <Tooltip title="Fit to view" placement="left">
                    <IconButton size="small" onClick={handleGraphZoomFit}>
                      <CenterFocusStrong fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Paper>

                {graphDimensions.h > 0 && (
                  <ForceGraph2D
                    ref={(el) => {
                      documentGraphRef.current = el;
                    }}
                    graphData={renderedGraphData}
                    width={graphDimensions.w}
                    height={graphDimensions.h}
                    backgroundColor="#f0f4f8"
                    nodeCanvasObject={(node, ctx, globalScale) => {
                      const isSelected = selectedGraphNode?.id === node.id;
                      const isHovered = Boolean(graphHoveredNode && graphHoveredNode.id === node.id);
                      const nodeSearchText = `${node?.label || ''} ${node?.name || ''} ${node?.id || ''}`.toLowerCase();
                      const isSearchMatch = Boolean(normalizedGraphSearchTerm) && nodeSearchText.includes(normalizedGraphSearchTerm);
                      const color = node.color || '#2196F3';
                      const size = isSelected ? 14 : (isSearchMatch ? 10 : 8);

                      if (isSearchMatch && !isSelected) {
                        ctx.beginPath();
                        ctx.arc(node.x, node.y, size + 3.5, 0, 2 * Math.PI);
                        ctx.strokeStyle = 'rgba(255,152,0,0.65)';
                        ctx.lineWidth = 2;
                        ctx.stroke();
                      }

                      ctx.beginPath();
                      ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
                      ctx.fillStyle = isSelected ? withAlpha(color, 1) : withAlpha(color, 0.87);
                      ctx.fill();
                      ctx.strokeStyle = isSelected ? '#fff' : 'rgba(255,255,255,0.5)';
                      ctx.lineWidth = isSelected ? 2.5 : 1.5;
                      ctx.stroke();

                      if (!isSelected && !isHovered) return;

                      const rawLabel = node.label || node.name || '';
                      const fontSize = Math.max(10 / globalScale, isSelected ? 5 : 4);
                      const fontWeight = isSelected ? 'bold' : '500';
                      ctx.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
                      ctx.textAlign = 'center';
                      ctx.textBaseline = 'top';

                      const maxLineWidth = Math.max(80, size * 8);
                      const words = rawLabel.split(' ');
                      const lines = [];
                      let current = '';

                      for (const word of words) {
                        const test = current ? `${current} ${word}` : word;
                        if (ctx.measureText(test).width > maxLineWidth && current) {
                          lines.push(current);
                          current = word;
                        } else {
                          current = test;
                        }
                      }

                      if (current) lines.push(current);
                      if (lines.length > 3) {
                        lines.splice(3);
                        lines[2] = `${lines[2].slice(0, Math.max(0, lines[2].length - 1))}\u2026`;
                      }

                      const lineH = fontSize + 1;
                      const boxW = Math.max(...lines.map((line) => ctx.measureText(line).width));
                      const labelY = node.y + size + 4;
                      const padX = 4;
                      const padY = 2;
                      const radius = 3;
                      const bx = node.x - boxW / 2 - padX;
                      const by = labelY - padY;
                      const bw = boxW + padX * 2;
                      const bh = lines.length * lineH + padY * 2;

                      ctx.fillStyle = isSelected ? 'rgba(255,248,200,0.95)' : 'rgba(255,255,255,0.92)';
                      ctx.beginPath();
                      ctx.moveTo(bx + radius, by);
                      ctx.lineTo(bx + bw - radius, by);
                      ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + radius);
                      ctx.lineTo(bx + bw, by + bh - radius);
                      ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - radius, by + bh);
                      ctx.lineTo(bx + radius, by + bh);
                      ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - radius);
                      ctx.lineTo(bx, by + radius);
                      ctx.quadraticCurveTo(bx, by, bx + radius, by);
                      ctx.closePath();
                      ctx.fill();

                      if (isSelected) {
                        ctx.strokeStyle = 'rgba(200,160,0,0.5)';
                        ctx.lineWidth = 0.8;
                        ctx.stroke();
                      }

                      ctx.fillStyle = isSelected ? '#7a5900' : '#333';
                      lines.forEach((line, i) => {
                        ctx.fillText(line, node.x, labelY + i * lineH);
                      });
                    }}
                    nodeCanvasObjectMode={() => 'replace'}
                    linkCanvasObject={(link, ctx, globalScale) => {
                      const src = link.source;
                      const tgt = link.target;
                      if (!src || !tgt || typeof src.x === 'undefined' || typeof tgt.x === 'undefined') return;

                      const isSelectedEdge = selectedGraphEdge?.id === link.id;
                      const isHoveredEdge = graphHoveredEdge?.id === link.id;
                      const selectedNodeInvolved = selectedGraphNode && (
                        ((typeof link.source === 'object' ? link.source?.id : link.source) === selectedGraphNode.id)
                        || ((typeof link.target === 'object' ? link.target?.id : link.target) === selectedGraphNode.id)
                      );
                      const color = link.color || EDGE_COLORS.default;
                      const dx = tgt.x - src.x;
                      const dy = tgt.y - src.y;
                      const dist = Math.sqrt(dx * dx + dy * dy) || 1;

                      const curvature = 0.2;
                      const cpX = (src.x + tgt.x) / 2 - curvature * dy;
                      const cpY = (src.y + tgt.y) / 2 + curvature * dx;

                      ctx.beginPath();
                      ctx.moveTo(src.x, src.y);
                      ctx.quadraticCurveTo(cpX, cpY, tgt.x, tgt.y);
                      ctx.strokeStyle = isSelectedEdge
                        ? withAlpha(color, 0.95)
                        : (isHoveredEdge || selectedNodeInvolved ? withAlpha(color, 0.8) : withAlpha(color, 0.45));
                      ctx.lineWidth = isSelectedEdge ? 3.2 : (isHoveredEdge || selectedNodeInvolved ? 2.4 : 1.8);
                      ctx.stroke();

                      const t = 0.78;
                      const arrowX = (1 - t) * (1 - t) * src.x + 2 * (1 - t) * t * cpX + t * t * tgt.x;
                      const arrowY = (1 - t) * (1 - t) * src.y + 2 * (1 - t) * t * cpY + t * t * tgt.y;
                      const tanX = 2 * (1 - t) * (cpX - src.x) + 2 * t * (tgt.x - cpX);
                      const tanY = 2 * (1 - t) * (cpY - src.y) + 2 * t * (tgt.y - cpY);
                      const angle = Math.atan2(tanY, tanX);
                      const arrowLen = 7;

                      ctx.beginPath();
                      ctx.moveTo(arrowX, arrowY);
                      ctx.lineTo(arrowX - arrowLen * Math.cos(angle - Math.PI / 6), arrowY - arrowLen * Math.sin(angle - Math.PI / 6));
                      ctx.lineTo(arrowX - arrowLen * Math.cos(angle + Math.PI / 6), arrowY - arrowLen * Math.sin(angle + Math.PI / 6));
                      ctx.closePath();
                      ctx.fillStyle = isSelectedEdge ? withAlpha(color, 0.95) : withAlpha(color, 0.65);
                      ctx.fill();

                      if (link.label && globalScale >= 0.6 && (isSelectedEdge || isHoveredEdge || selectedNodeInvolved)) {
                        const midX = (src.x + tgt.x) / 2 - curvature * dy * 0.5;
                        const midY = (src.y + tgt.y) / 2 + curvature * dx * 0.5;
                        const perpOffsetDist = 10 / globalScale;
                        const labelX = midX - (dy / dist) * perpOffsetDist;
                        const labelY = midY + (dx / dist) * perpOffsetDist;

                        const fontSize = Math.max(9 / globalScale, 4);
                        ctx.font = `500 ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';

                        const textWidth = ctx.measureText(link.label).width;
                        const padX = 3;
                        const padY = 1.5;
                        ctx.fillStyle = 'rgba(255,255,255,0.88)';
                        const rx = labelX - textWidth / 2 - padX;
                        const ry = labelY - fontSize / 2 - padY;
                        const rw = textWidth + padX * 2;
                        const rh = fontSize + padY * 2;
                        ctx.beginPath();
                        ctx.roundRect(rx, ry, rw, rh, 3);
                        ctx.fill();

                        ctx.fillStyle = `${color}CC`;
                        ctx.fillText(link.label, labelX, labelY);
                      }
                    }}
                    linkCanvasObjectMode={() => 'replace'}
                    nodeLabel={(node) => {
                      const entityTypeText = node.entityType ? `, entity_type: ${node.entityType}` : '';
                      return `${node.name} [${node.type || 'unknown'}${entityTypeText}] (mentions: ${node.mentions || 1})`;
                    }}
                    linkDirectionalArrowLength={0}
                    enableNodeDrag
                    enableZoomInteraction
                    enablePanInteraction
                    cooldownTicks={200}
                    d3AlphaDecay={0.02}
                    d3VelocityDecay={0.3}
                    d3AlphaMin={0.001}
                    nodeRelSize={7}
                    warmupTicks={50}
                    onNodeClick={handleGraphNodeClick}
                    onNodeHover={(node) => setGraphHoveredNode(node || null)}
                    onLinkClick={handleGraphLinkClick}
                    onLinkHover={(link) => setGraphHoveredEdge(link || null)}
                    onNodeDragEnd={(node) => {
                      node.fx = node.x;
                      node.fy = node.y;
                      const resolvedNode = graphNodeMap[node.id] || node;
                      setSelectedGraphEdge(null);
                      setSelectedGraphNode(resolvedNode);
                    }}
                    onBackgroundClick={() => {
                      setSelectedGraphNode(null);
                      setSelectedGraphEdge(null);
                    }}
                    onEngineStart={() => {
                      if (documentGraphRef.current) {
                        documentGraphRef.current.d3Force('charge').strength(graphLayoutMode === 'compact' ? -520 : -850).distanceMax(graphLayoutMode === 'compact' ? 520 : 850);
                        const linkForce = documentGraphRef.current.d3Force('link');
                        if (linkForce) {
                          linkForce.distance(graphLayoutMode === 'compact' ? 150 : 260).strength(graphLayoutMode === 'compact' ? 0.38 : 0.28);
                        }
                        const centerForce = documentGraphRef.current.d3Force('center');
                        if (centerForce) {
                          centerForce.x(0).y(0);
                        }
                      }
                    }}
                    onEngineStop={() => {
                      if (documentGraphRef.current && renderedGraphData.nodes.length > 0) {
                        documentGraphRef.current.zoomToFit(400, 40);
                      }
                    }}
                  />
                )}
              </Box>

              <Box sx={{ width: 300, minWidth: 300, borderLeft: '1px solid', borderColor: 'divider', overflow: 'auto', p: 1.25, bgcolor: '#fff' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Inspector
                  </Typography>

                  <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {`Visible nodes: ${filteredGraphData.nodes.length}/${graphCoverageSummary.nodeCount}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {`Visible edges: ${filteredGraphData.links.length}/${graphCoverageSummary.edgeCount}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {`Isolated nodes: ${graphCoverageSummary.isolatedCount}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {`Mentions total: ${graphCoverageSummary.mentionsTotal}`}
                    </Typography>
                  </Paper>

                  {selectedGraphEdge ? (
                    <>
                      <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                        <Chip
                          size="small"
                          label={selectedGraphEdge.label || 'related_to'}
                          sx={{ mb: 0.75, fontWeight: 700, bgcolor: selectedGraphEdge.color || '#607d8b', color: '#fff' }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                          {`${selectedGraphEdgeEndpoints.source?.label || selectedGraphEdge.source} -> ${selectedGraphEdgeEndpoints.target?.label || selectedGraphEdge.target}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Confidence: {selectedGraphEdge.confidence != null ? selectedGraphEdge.confidence.toFixed(3) : '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Inferred: {selectedGraphEdge.isInferred ? 'Yes' : 'No'}
                        </Typography>
                        {selectedGraphEdge.documentTitle && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                            Document: {selectedGraphEdge.documentTitle}
                          </Typography>
                        )}
                        {selectedGraphEdge.chunkPageNumber ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Page: {selectedGraphEdge.chunkPageNumber}
                          </Typography>
                        ) : null}
                        {selectedGraphEdge.evidenceText && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, wordBreak: 'break-word' }}>
                            Evidence: {selectedGraphEdge.evidenceText}
                          </Typography>
                        )}
                        {selectedGraphEdge.metadata && Object.keys(selectedGraphEdge.metadata).length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, wordBreak: 'break-word' }}>
                            Metadata: {JSON.stringify(selectedGraphEdge.metadata)}
                          </Typography>
                        )}
                        {selectedGraphEdge.provenance && Object.keys(selectedGraphEdge.provenance).length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, wordBreak: 'break-word' }}>
                            Provenance: {JSON.stringify(selectedGraphEdge.provenance)}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                          {selectedGraphEdgeEndpoints.source && (
                            <Button size="small" variant="outlined" onClick={() => navigateToGraphNode(selectedGraphEdgeEndpoints.source)}>
                              Source
                            </Button>
                          )}
                          {selectedGraphEdgeEndpoints.target && (
                            <Button size="small" variant="outlined" onClick={() => navigateToGraphNode(selectedGraphEdgeEndpoints.target)}>
                              Target
                            </Button>
                          )}
                          <Button size="small" variant="outlined" onClick={() => handleViewDocument(graphDocument)} disabled={!graphDocument?.id}>
                            Open Doc
                          </Button>
                        </Stack>
                      </Paper>

                      {Array.isArray(selectedGraphEdge.mentions) && selectedGraphEdge.mentions.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Mentions ({selectedGraphEdge.mentions.length})
                          </Typography>
                          <List dense disablePadding sx={{ maxHeight: 260, overflow: 'auto' }}>
                            {selectedGraphEdge.mentions.map((mention) => (
                              <ListItemButton key={`edge-mention-${mention.id}`} sx={{ py: 0.35, px: 0.5 }} onClick={() => handleViewDocument(graphDocument)}>
                                <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                                  {`${mention.document_title || 'Document'} · p.${mention.page_number || '-'}${mention.section ? ` · ${mention.section}` : ''}${mention.heading ? ` · ${mention.heading}` : ''}${mention.chunk ? ` · chunk ${mention.chunk}` : ''}`}
                                </Typography>
                              </ListItemButton>
                            ))}
                          </List>
                        </Paper>
                      )}
                    </>
                  ) : selectedGraphNode ? (
                    <>
                      <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                        {React.createElement(getEntityTypeIcon(selectedGraphNode.entityType || selectedGraphNode.type || 'unknown'), {
                          style: { color: selectedGraphNode.color || '#607d8b', marginBottom: 6, fontSize: '1.1rem' },
                        })}
                        <Chip
                          size="small"
                          label={formatEntityType(selectedGraphNode.entityType || selectedGraphNode.type || 'unknown')}
                          sx={{ mb: 0.75, fontWeight: 700, bgcolor: selectedGraphNode.color || '#607d8b', color: '#fff' }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                          {selectedGraphNode.label || selectedGraphNode.name || selectedGraphNode.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          ID: {selectedGraphNode.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Type: {formatEntityType(selectedGraphNode.entityType || selectedGraphNode.type || 'unknown')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Mentions: {selectedGraphNode.mentions || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Degree: {selectedGraphNode.degree || 0}
                        </Typography>
                        {selectedGraphNode.confidence != null && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Confidence: {selectedGraphNode.confidence.toFixed(3)}
                          </Typography>
                        )}
                        {selectedGraphNode.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word', mt: 0.3 }}>
                            Description: {selectedGraphNode.description}
                          </Typography>
                        )}
                        {selectedGraphNode.canonicalName && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                            Canonical: {selectedGraphNode.canonicalName}
                          </Typography>
                        )}
                        {Array.isArray(selectedGraphNode.aliases) && selectedGraphNode.aliases.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                            Aliases: {selectedGraphNode.aliases.join(', ')}
                          </Typography>
                        )}
                        {selectedGraphNode.properties && Object.keys(selectedGraphNode.properties).length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, wordBreak: 'break-word' }}>
                            Properties: {JSON.stringify(selectedGraphNode.properties)}
                          </Typography>
                        )}
                        {selectedGraphNode.externalIds && Object.keys(selectedGraphNode.externalIds).length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, wordBreak: 'break-word' }}>
                            External IDs: {JSON.stringify(selectedGraphNode.externalIds)}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                          <Button size="small" variant="outlined" onClick={() => handleViewDocument(graphDocument)} disabled={!graphDocument?.id}>
                            Open Doc
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => setGraphFocusMode('neighbors')}>
                            Focus Neighbors
                          </Button>
                        </Stack>
                      </Paper>

                      {Array.isArray(selectedGraphNode.documents) && selectedGraphNode.documents.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Documents ({selectedGraphNode.documents.length})
                          </Typography>
                          <List dense disablePadding sx={{ maxHeight: 180, overflow: 'auto' }}>
                            {selectedGraphNode.documents.map((doc) => (
                              <ListItemButton key={`doc-${selectedGraphNode.id}-${doc.document_id}`} sx={{ py: 0.25, px: 0.5 }} onClick={() => handleViewDocument(graphDocument)}>
                                <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                                  {`${doc.document_title || 'Document'} · pages ${Array.isArray(doc.pages) && doc.pages.length > 0 ? doc.pages.join(', ') : '-'}${Array.isArray(doc.sections) && doc.sections.length > 0 ? ` · ${doc.sections.join(', ')}` : ''}`}
                                </Typography>
                              </ListItemButton>
                            ))}
                          </List>
                        </Paper>
                      )}

                      {Array.isArray(selectedGraphNode.mentionItems) && selectedGraphNode.mentionItems.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Mentions ({selectedGraphNode.mentionItems.length})
                          </Typography>
                          <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto' }}>
                            {selectedGraphNode.mentionItems.map((mention) => (
                              <ListItemButton key={`mention-${mention.id}`} sx={{ py: 0.25, px: 0.5 }} onClick={() => handleViewDocument(graphDocument)}>
                                <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                                  {`${mention.document_title || 'Document'} · p.${mention.page_number || '-'}${mention.heading ? ` · ${mention.heading}` : ''}${mention.chunk ? ` · chunk ${mention.chunk}` : ''}`}
                                </Typography>
                              </ListItemButton>
                            ))}
                          </List>
                        </Paper>
                      )}

                      {selectedGraphNodeRelationships.incoming.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Incoming ({selectedGraphNodeRelationships.incoming.length})
                          </Typography>
                          <List dense disablePadding sx={{ maxHeight: 180, overflow: 'auto' }}>
                            {selectedGraphNodeRelationships.incoming.map(({ node, link }, index) => (
                              <ListItemButton key={`in-${index}`} sx={{ py: 0.2, px: 0.5 }} onClick={() => navigateToGraphNode(node)}>
                                <Chip
                                  size="small"
                                  label={getEntityTypeShort(node.entityTypeRaw || node.entityType || 'unknown')}
                                  sx={{ height: 16, fontSize: '0.58rem', mr: 0.5, bgcolor: node.color || '#607d8b', color: '#fff', fontWeight: 700 }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    position: 'relative',
                                    display: 'block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    pr: 1,
                                    '&::after': {
                                      content: '" |"',
                                      position: 'absolute',
                                      right: 0,
                                      top: 0,
                                      color: 'text.disabled',
                                      fontWeight: 700,
                                    },
                                  }}
                                  noWrap
                                >
                                  {`${node.label || node.name || node.id} <- ${link?.label || 'related_to'}`}
                                </Typography>
                              </ListItemButton>
                            ))}
                          </List>
                        </Paper>
                      )}

                      {selectedGraphNodeRelationships.outgoing.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Outgoing ({selectedGraphNodeRelationships.outgoing.length})
                          </Typography>
                          <List dense disablePadding sx={{ maxHeight: 180, overflow: 'auto' }}>
                            {selectedGraphNodeRelationships.outgoing.map(({ node, link }, index) => (
                              <ListItemButton key={`out-${index}`} sx={{ py: 0.2, px: 0.5 }} onClick={() => navigateToGraphNode(node)}>
                                <Chip
                                  size="small"
                                  label={getEntityTypeShort(node.entityTypeRaw || node.entityType || 'unknown')}
                                  sx={{ height: 16, fontSize: '0.58rem', mr: 0.5, bgcolor: node.color || '#607d8b', color: '#fff', fontWeight: 700 }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    position: 'relative',
                                    display: 'block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    pr: 1,
                                    '&::after': {
                                      content: '" |"',
                                      position: 'absolute',
                                      right: 0,
                                      top: 0,
                                      color: 'text.disabled',
                                      fontWeight: 700,
                                    },
                                  }}
                                  noWrap
                                >
                                  {`${link?.label || 'related_to'} -> ${node.label || node.name || node.id}`}
                                </Typography>
                              </ListItemButton>
                            ))}
                          </List>
                        </Paper>
                      )}
                    </>
                  ) : (
                    <Alert severity="info">Select a node or edge to inspect provenance and connected facts.</Alert>
                  )}
                </Box>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {`Pipeline: NEW (legacy disabled) | Visual nodes/edges: ${filteredGraphData.nodes.length}/${graphData.nodes.length} | ${filteredGraphData.links.length}/${graphData.links.length} | Persisted nodes/edges: ${graphPersistedSummary.nodes}/${graphPersistedSummary.edges} | Isolated: ${graphCoverageSummary.isolatedCount} | Mentions: ${graphCoverageSummary.mentionsTotal} | Extractor: ${graphPayload?.meta?.extractor || 'unknown'}`}
          </Typography>
          {graphExtractionRun && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {`Extraction run: ${graphExtractionRun.run_id || graphExtractionRun.id} | status: ${graphExtractionRun.status || 'unknown'}${graphExtractionRun.model_name ? ` | model: ${graphExtractionRun.model_name}` : ''}`}
            </Typography>
          )}
          {graphExtractionRun && graphRunDetailRows.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 1, maxWidth: 640 }}>
              <Box sx={{ px: 1.25, pt: 1, pb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  KG Run details
                </Typography>
              </Box>
              <Table size="small" aria-label="KG run details">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '45%', fontWeight: 700 }}>Metric</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {graphRunDetailRows.map((row) => (
                    <TableRow
                      key={row.label}
                      sx={(theme) => {
                        const valueNum = Number(row.value);
                        const shouldHighlight =
                          (row.label === 'Pages failed' || row.label === 'Errors')
                          && Number.isFinite(valueNum)
                          && valueNum > 0;
                        if (!shouldHighlight) return {};
                        return {
                          '& td': {
                            color: theme.palette.error.main,
                            fontWeight: 700,
                          },
                        };
                      }}
                    >
                      <TableCell>{row.label}</TableCell>
                      <TableCell>{String(row.value ?? '-')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Tooltip
            title={
              hasGraphVisualization
                ? 'Open LangExtract HTML visualization'
                : 'Visualization not available yet. Regenerate graph with LANGEXTRACT_SAVE_VISUALIZATION=true'
            }
          >
            <span>
              <Button
                startIcon={<VisibilityOutlined />}
                onClick={() => handleViewGraphVisualization(graphDocument)}
                disabled={graphLoading || !graphDocument?.id || !hasGraphVisualization}
              >
                View New KG Visualization (Temp)
              </Button>
            </span>
          </Tooltip>
          <Button
            startIcon={<Refresh />}
            onClick={() => handleOpenGraph(graphDocument, true)}
            disabled={graphLoading || !graphDocument?.id}
          >
            Regenerate New KG
          </Button>
          <Button onClick={handleCloseGraphDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentsTab;
